import posthog from "posthog-js";

/**
 * Search Analytics Module
 *
 * Tracks user interactions with the navigation search component to understand:
 * - How users open search (keyboard vs click)
 * - What they search for (query length, not content for privacy)
 * - What results they select
 * - How they navigate (keyboard vs mouse)
 * - Session duration
 */

export type SearchOpenMethod =
  | "keyboard_shortcut"
  | "click"
  | "floating_button";
export type SearchCloseMethod =
  | "keyboard_shortcut"
  | "click_outside"
  | "selection"
  | "backdrop_click";
export type SearchResultType =
  | "patient"
  | "page"
  | "tenant"
  | "location"
  | "theme"
  | "action"
  | "product"
  | "service"
  | "membership"
  | "package"
  | "medication"
  | "gift_card"
  | "recent_patient"
  | "recent_page"
  | "suggested_page"
  | "create_patient";
export type KeyboardNavigationAction =
  | "arrow_down"
  | "arrow_up"
  | "enter_select"
  | "escape_close";

interface SearchSession {
  openedAt: number;
  openMethod: SearchOpenMethod;
  searchQueries: number;
  keyboardNavigations: number;
  maxQueryLength: number;
}

let currentSession: SearchSession | null = null;

/**
 * Track when search is opened
 */
export function trackSearchOpened(method: SearchOpenMethod) {
  currentSession = {
    openedAt: Date.now(),
    openMethod: method,
    searchQueries: 0,
    keyboardNavigations: 0,
    maxQueryLength: 0,
  };

  posthog.capture("navigation_search_opened", {
    method,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track when search is closed
 */
export function trackSearchClosed(method: SearchCloseMethod) {
  if (!currentSession) {
    return;
  }

  const durationMs = Date.now() - currentSession.openedAt;

  posthog.capture("navigation_search_closed", {
    method,
    duration_ms: durationMs,
    search_queries: currentSession.searchQueries,
    keyboard_navigations: currentSession.keyboardNavigations,
    max_query_length: currentSession.maxQueryLength,
    open_method: currentSession.openMethod,
  });

  currentSession = null;
}

/**
 * Track when a search query is performed
 */
export function trackSearchQuery(queryLength: number) {
  if (currentSession) {
    currentSession.searchQueries++;
    currentSession.maxQueryLength = Math.max(
      currentSession.maxQueryLength,
      queryLength,
    );
  }

  // Only track when a meaningful search is performed (2+ chars)
  if (queryLength >= 2) {
    posthog.capture("navigation_search_query", {
      query_length: queryLength,
    });
  }
}

/**
 * What the user saw when they picked a result. Built by the caller with
 * patients already redacted: `query` is null when it may be a patient's name,
 * phone or email, and patient options are `patient:<id>`, never a name.
 */
export interface SearchSelectionContext {
  query: string | null;
  options: string[];
}

/** PostHog properties stay small; nobody scrolls past this many rows. */
const MAX_OPTIONS_TRACKED = 50;

/**
 * Track when a search result is selected
 */
export function trackResultSelected(
  resultType: SearchResultType,
  selectionMethod: "keyboard" | "click",
  queryLength: number,
  resultIndex: number,
  actionId?: string,
  context?: SearchSelectionContext,
) {
  if (!currentSession) {
    // A result should only be selected within an active search session.
    // If not, it's likely a state issue, so we don't track to avoid bad data.
    return;
  }

  const durationMs = Date.now() - currentSession.openedAt;

  const eventProperties: Record<string, unknown> = {
    result_type: resultType,
    selection_method: selectionMethod,
    query_length: queryLength,
    result_index: resultIndex,
    duration_ms: durationMs,
    keyboard_navigations: currentSession.keyboardNavigations,
  };

  // Include action ID if this is an action selection
  if (resultType === "action" && actionId) {
    eventProperties.action_id = actionId;
  }

  posthog.capture("navigation_search_result_selected", eventProperties);

  // The top result wasn't what the user wanted: a signal that ranking could
  // be better for this query/result type.
  if (resultIndex > 0) {
    posthog.capture("navigation_search_non_first_result_selected", {
      ...eventProperties,
      position: resultIndex + 1,
      ...(context && {
        query: context.query,
        query_redacted: context.query === null && queryLength > 0,
        options: context.options.slice(0, MAX_OPTIONS_TRACKED),
        option_count: context.options.length,
        selected_option: context.options[resultIndex] ?? null,
        first_option: context.options[0] ?? null,
      }),
    });
  }
}

/**
 * Track keyboard navigation within search
 */
export function trackKeyboardNavigation(action: KeyboardNavigationAction) {
  if (currentSession) {
    currentSession.keyboardNavigations++;
  }

  posthog.capture("navigation_search_keyboard_nav", {
    action,
  });
}

/**
 * Check if a search session is currently active
 */
export function isSearchSessionActive(): boolean {
  return currentSession !== null;
}
